/**
 * "The place today": one place, read-only but for scheduling an event, top to
 * bottom — the room now, the schedule, how the last ones went, and the parts.
 * Pure presentation (no data fetching, no router), so the proof script can
 * render it from fixtures.
 * The room now (unit 1) lists every room with its game, faces, headcount and
 * rules preset; the page re-reads it on a timer (`use-places.ts`).
 * How the last ones went (unit 2) lists the last events begun in the place's
 * seasons with who came, the tables opened and the top three; read once.
 * People (unit 3) counts the place's members (the people following its
 * seasons) on the People card and in the section, and lists them by season
 * only when the partner opens them (collapsed by default); each read once. The live
 * headcount ("N here now") stays in the header and on the Turnstile card,
 * never mixed with members.
 * Standings (unit 4) rank each season's top ten (rank, face, name, points) in a
 * section collapsed by default, and put the leader of the season running most
 * recently on the Leaderboard card; that season read once up front, the others
 * once when the partner opens the tables. No place-wide roll-up exists.
 * Games (unit 5) count the games the place can run on the Games card (its
 * brand's game list joined to the catalog; a house place: every enabled game)
 * and, in a section collapsed by default, say what its rooms start on, the
 * games it runs and what is playing now (from the room-now read); read once.
 * Schedule (unit 6) says what is next up, collapsed by default to the next
 * one; opened, it lists the next ten, then the repeats and the templates tied
 * to the place, those two read only for pop.in staff, on first open.
 * Scheduling an event (unit 6b) is the one write: "Schedule an event" on the
 * Schedule header (pop.in staff on a dev or preview build only) opens the
 * form inline; the event made shows under Next up with "Undo" for five
 * minutes (`schedule-event-form.tsx`, `schedule-event.ts`).
 * Changing or cancelling one (unit 6c) is the same people's: each row still
 * to come gets "Change" (the same form, filled in from the event) and
 * "Cancel" (one review line, then "Cancel it"), Cancel held within a minute
 * of the start; a started row says the admin console runs it. The change or
 * cancel carries "Undo" for five minutes (`change-event.ts`).
 * The Turnstile (unit 9) puts the place's numbers in one section, collapsed by
 * default, from reads the page already makes (nothing read of its own): right
 * now (here now, tables open, what is playing: the room-now read), the last
 * events' visitors and tables (the last-events read), and, said plainly, what
 * is not kept: an all-time visitor count, and anything over time.
 */
import * as React from "react";
import {
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  DoorOpen,
  Gamepad2,
  History,
  LayoutPanelTop,
  Palette,
  ShoppingBag,
  Trophy,
  Users,
  type LucideIcon,
} from "lucide-react";

import { cn, initialsOf, plural } from "@/lib/utils";
import { readAtLabel, whenLabel } from "@/lib/dates";
import type { PlaceData } from "@/data/places-api";
import type { PlaceSeasonsData } from "@/data/place-events-api";
import { LATELY_DAYS, eventRowsOf, type EventRow, type PlaceEvents } from "@/features/places/last-events";
import { faceOf, type Face } from "@/features/places/face";
import type { PlaceMembers, SeasonMembers } from "@/features/places/members";
import { STANDINGS_SHOWN, leaderOf, type Leader, type PlaceStandings, type SeasonStandings } from "@/features/places/standings";
import { unknownWords, type GameRow, type PlaceGames, type StartsOn } from "@/features/places/games";
import {
  DEFAULT_DURATION_MS,
  durationLabel,
  repeatWhenLabel,
  untiedLine,
  type NextUp,
  type NextUpRow,
  type PlaceSchedule,
  type RepeatRow,
  type StaffGroup,
  type TemplateRow,
} from "@/features/places/schedule";
import { TURNSTILE_LATER, allTimeLine, cameNote, lastEventsTitle, turnstileFooter, withSource } from "@/features/places/turnstile";
import { useNow } from "@/lib/use-now";
import { undoWindowOpen } from "@/features/writes/writes";
import {
  UNDO_LAPSED_LINE,
  canUndo,
  madeNotShownLine,
  undoneLine,
  wasUndone,
  type MadeEvent,
} from "@/features/places/schedule-event";
import {
  STARTED_LINE,
  TOO_CLOSE_LINE,
  canUndoChange,
  changeUndone,
  changedLine,
  rowWriteOf,
  undoChangeProblem,
  undoable,
  type ChangedEvent,
} from "@/features/places/change-event";
import { CancelEventReview, ScheduleEventForm, type CancelStep, type FormStep } from "@/features/places/schedule-event-form";
import type { Scheduling } from "@/features/places/use-schedule-event";
import type { Load } from "@/features/places/use-places";
import { Avatar, AvatarFallback, AvatarGroup, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/** A "sample" chip beside anything showing fixture numbers. */
export function SampleChip() {
  return (
    <span className="rounded-md border border-dashed border-border-strong bg-panel-2 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[.08em] text-text-3">
      sample
    </span>
  );
}

/** A small outlined label (a rules preset, "Default"). */
export function PillBadge({ children }: { children: React.ReactNode }) {
  return (
    <Badge variant="outline" className="rounded-md bg-panel-2 px-1.5 py-px text-[10.5px] font-semibold text-muted-foreground">
      {children}
    </Badge>
  );
}

/** Green when someone is in the room, hollow otherwise. */
export function PresenceDot({ count }: { count: number }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-2 shrink-0 rounded-full",
        count > 0 ? "bg-live" : "border border-border-strong bg-transparent",
      )}
    />
  );
}

/** How many faces a row shows before "+N". */
export const FACES_SHOWN = 6;

export type { Face };

export interface RoomRow {
  id: string;
  label: string;
  title: string;
  /** The room's game by name, or null when it has none. */
  activity: string | null;
  /** What tells the room's game apart: its slug (its id when it has none);
   *  two games may share a name. Null when it has none. */
  activityKey: string | null;
  here: number;
  /** The first {@link FACES_SHOWN} people in the room. */
  faces: Face[];
  /** People in the room beyond the faces shown (the "+N"). */
  moreFaces: number;
  /** The room's rules preset by name, or null when it has none. */
  preset: string | null;
}

type Room = NonNullable<PlaceData["rootGathering"]>;
type Table = PlaceData["subGatherings"]["nodes"][number];

function roomRow(room: Room | Table, id: string, label: string, title: string): RoomRow {
  // "N here" is `onlineUsersCount`; the face list is a separate read that can
  // disagree for a moment (cache, repeat rows). Faces and "+N" follow the
  // headcount so the row never contradicts itself.
  const here = room.onlineUsersCount;
  const faces = room.gatheringUsers.nodes.slice(0, Math.min(FACES_SHOWN, here)).map(faceOf);
  return {
    id,
    label,
    title,
    activity: room.activity?.name ?? null,
    activityKey: room.activity ? room.activity.slug || room.activity.id : null,
    here,
    faces,
    moreFaces: Math.max(0, here - faces.length),
    preset: room.settings?.name ?? null,
  };
}

/** The main room first, then every open table by headcount (busiest first).
 *  Empty tables stay in the list. This is the first-load order; re-reads keep
 *  it with {@link roomsInOrder}. */
export function roomsOf(place: PlaceData): RoomRow[] {
  const root = place.rootGathering;
  const main = root ? [roomRow(root, root.id ?? "main", "Main room", root.title || place.name)] : [];
  const tables = place.subGatherings.nodes
    .map((table, i) => roomRow(table, table.id ?? `table-${i}`, "Table", table.title || `Table ${i + 1}`))
    // Stable sort: equal headcounts keep the backend's newest-first order.
    .sort((a, b) => b.here - a.here);
  return [...main, ...tables];
}

/** {@link roomsOf}, keeping the row order of an earlier read (`prev`: its row
 *  ids) so rows do not move under the reader every poll. The main room stays
 *  first; tables keep their earlier places; a table new since then is appended
 *  (busiest first); a table that closed drops out. `prev` null = first load. */
export function roomsInOrder(place: PlaceData, prev: readonly string[] | null): RoomRow[] {
  const rows = roomsOf(place);
  if (!prev) return rows;
  const at = new Map(prev.map((id, i) => [id, i]));
  const main = place.rootGathering ? rows.slice(0, 1) : [];
  const tables = rows.slice(main.length);
  const kept = tables.filter((r) => at.has(r.id)).sort((a, b) => at.get(a.id)! - at.get(b.id)!);
  const added = tables.filter((r) => !at.has(r.id));
  return [...main, ...kept, ...added];
}

/** What a headcount needs: the main room's count and every open table's
 *  count. Both the list row and the detail read carry it. */
export interface HeadcountSource {
  rootGathering: { onlineUsersCount: number } | null;
  subGatherings: { totalCount: number; nodes: readonly { onlineUsersCount: number }[] };
}

/** Everyone in the place right now: the main room plus every open table read. */
export function headcountOf(place: HeadcountSource): number {
  return place.subGatherings.nodes.reduce(
    (sum, table) => sum + table.onlineUsersCount,
    place.rootGathering?.onlineUsersCount ?? 0,
  );
}

/** Tables open beyond those the read returned (the backend sends 50 at most). */
export function tablesNotRead(place: HeadcountSource): number {
  return place.subGatherings.totalCount - place.subGatherings.nodes.length;
}

/** "+N more tables open, not read.": the open tables past the read. */
export function tablesNotReadLine(n: number): string {
  return `+${n.toLocaleString()} more ${plural(n, "table")} open, not read.`;
}

/** True when some open tables were not read ({@link tablesNotRead}), so
 *  {@link headcountOf} is a floor, not the full count. */
export function isHeadcountPartial(place: HeadcountSource): boolean {
  return tablesNotRead(place) > 0;
}

/** The headcount as shown: "N", or "N+" when some tables were not read. */
export function headcountLabel(place: HeadcountSource): string {
  return `${headcountOf(place).toLocaleString()}${isHeadcountPartial(place) ? "+" : ""}`;
}

function SectionHeader({
  icon: Icon,
  title,
  sample,
  action,
}: {
  icon: LucideIcon;
  title: string;
  sample: boolean;
  /** A button at the header's end (before the sample chip). */
  action?: React.ReactNode;
}) {
  return (
    <CardHeader data-section={title}>
      <CardTitle>
        <Icon /> {title}
      </CardTitle>
      {(action || sample) && (
        <span className="ml-auto flex items-center gap-2">
          {action}
          {sample && <SampleChip />}
        </span>
      )}
    </CardHeader>
  );
}

/** One person's face: their photo, or their initials when they have none. */
function FaceAvatar({ face }: { face: Face }) {
  return (
    <Avatar size="sm" title={face.name}>
      {face.photoURL && <AvatarImage src={face.photoURL} alt={face.name} className="object-cover" />}
      <AvatarFallback className="bg-panel-2 text-[9px] font-bold">{initialsOf(face.name) || "?"}</AvatarFallback>
    </Avatar>
  );
}

function Faces({ faces, more }: { faces: Face[]; more: number }) {
  if (faces.length === 0 && more === 0) return null;
  return (
    // The ring sits on the card, so it takes the card colour (set on the group:
    // the group's own ring class outranks one on each Avatar).
    <AvatarGroup className="-space-x-1.5 *:data-[slot=avatar]:ring-card">
      {faces.map((f) => (
        <FaceAvatar key={f.id} face={f} />
      ))}
      {more > 0 && <span className="ml-3 self-center text-[11.5px] font-semibold text-text-3">+{more}</span>}
    </AvatarGroup>
  );
}

/** "updated Ns ago", ticking once a second. */
export function updatedAgo(readAt: number, now: number): string {
  const s = Math.max(0, Math.round((now - readAt) / 1000));
  return s < 60 ? `updated ${s}s ago` : `updated ${Math.floor(s / 60)}m ago`;
}

function UpdatedLine({ readAt, refreshFailed }: { readAt: number; refreshFailed: boolean }) {
  // One timer for every "updated" line on the page (`use-now.ts`).
  const now = useNow();
  return (
    <p className="pt-2 text-[11.5px] text-text-3">
      {updatedAgo(readAt, now)}
      {refreshFailed && " · the last refresh failed, retrying"}
    </p>
  );
}

/** When the live numbers were read, for the "updated Ns ago" line. */
export interface Freshness {
  readAt: number;
  refreshFailed: boolean;
}

function RoomNow({ place, sample, freshness }: { place: PlaceData; sample: boolean; freshness?: Freshness }) {
  // The row order of the last render, per place, so polls never reorder rows.
  const order = React.useRef<{ placeId: string; ids: string[] } | null>(null);
  const prev = order.current?.placeId === place.id ? order.current.ids : null;
  const rows = roomsInOrder(place, prev);
  order.current = { placeId: place.id, ids: rows.map((r) => r.id) };
  const hidden = tablesNotRead(place);
  return (
    <Card>
      <SectionHeader icon={DoorOpen} title="The room now" sample={sample} />
      <CardContent className="pt-3">
        {!place.rootGathering && (
          <p className="mb-2 text-[13px] text-muted-foreground">
            The main room is still being set up. It appears here within a minute.
          </p>
        )}
        <ul className="flex flex-col divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5">
              <PresenceDot count={r.here} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-semibold">{r.title}</div>
                <div className="flex items-center gap-1.5 text-[11.5px] text-text-3">
                  {r.label}
                  {r.preset && <PillBadge>{r.preset}</PillBadge>}
                </div>
              </div>
              <Faces faces={r.faces} more={r.moreFaces} />
              <div className={cn("text-[13px]", r.activity ? "text-foreground" : "text-text-3")}>
                {r.activity ?? "No game on"}
              </div>
              <div className="w-[72px] text-right font-mono text-[13px] tabular-nums">
                {r.here} <span className="text-text-3">here</span>
              </div>
            </li>
          ))}
        </ul>
        {hidden > 0 && <p className="pt-2 text-[12px] text-text-3">{tablesNotReadLine(hidden)}</p>}
        {freshness && <UpdatedLine {...freshness} />}
      </CardContent>
    </Card>
  );
}

/** A dashed box saying plainly why a section has nothing to show. */
function EmptyLine({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-[10px] border border-dashed border-border-strong bg-panel-2 px-3.5 py-3 text-[13px] text-muted-foreground">
      {children}
    </p>
  );
}

/** "N came", "N tables": a dash where the count is absent. */
function Count({ n, unit }: { n: number | null; unit: string }) {
  return (
    <div className="w-[84px] text-right font-mono text-[13px] tabular-nums">
      {n === null ? <span className="text-text-3">—</span> : n.toLocaleString()} <span className="text-text-3">{unit}</span>
    </div>
  );
}

/** The top three, best first, as faces with names. */
function TopThree({ row }: { row: EventRow }) {
  if (row.unread) return <span className="text-text-3">results did not read</span>;
  if (row.top === null) return <span className="text-text-3">—</span>;
  if (row.top.length === 0) return <span className="text-text-3">no scores</span>;
  return (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {row.top.map((f) => (
        <li key={f.id} className="flex min-w-0 items-center gap-1.5">
          <FaceAvatar face={f} />
          <span className="max-w-[110px] truncate">{f.name}</span>
        </li>
      ))}
    </ol>
  );
}

/** True when a read failed because the reader is signed out. */
function signedOut(message: string): boolean {
  return /signed in|sign in|unauthenticated|not authenticated/i.test(message);
}

/** What a section says when its read failed (`noun`: "the last events"):
 *  never the raw error (that goes to the console), and its own line when the
 *  reader is signed out. */
export function sectionErrorLine(noun: string, message: string): string {
  if (signedOut(message)) return `You are signed out. Sign in again to see ${noun}.`;
  return `Could not read ${noun}. Please try again in a moment.`;
}

/** The line under a section: its own lines (any left blank are skipped), then
 *  when it was read. */
export function sectionFooter(load: Load<unknown>, lines: readonly (string | null | false | undefined)[]): string {
  const parts = lines.filter((l): l is string => !!l);
  if (load.state === "ready" && load.readAt !== null) parts.push(`Read at ${readAtLabel(load.readAt)}.`);
  return parts.join(" ");
}

/** Why the list is empty. When the read was partial (only the most recent
 *  seasons read, or a season's read stopped at its page cap), it says so
 *  rather than claiming nothing has begun. */
export function noRowsLine(data: PlaceEvents): string {
  if (data.seasons.cut) {
    return `Only the ${data.seasons.read} most recent seasons were read; older ones may have events.`;
  }
  if (data.seasons.capped) return "Only the newest events of each season were read; older ones may have begun.";
  if (data.notShown > 0) return "The events that have begun here are not shown to you.";
  return "No event has begun in this place's seasons yet.";
}

/** The line under the section: where the rows come from, what is left out,
 *  and when they were read. */
export function lastOnesFooter(events: Load<PlaceEvents | null>): string {
  const data = events.state === "ready" ? events.data : null;
  return sectionFooter(events, [
    !data?.seasons.cut ? "From this place's seasons." : data.seasons.read > 0 && `From the ${data.seasons.read} most recent seasons here.`,
    data && data.notShown > 0 && "Some events here are not shown to you, so they are left out.",
    "Results per game and trends come later.",
  ]);
}

/** Why a list of the last events has no rows: still reading, the read
 *  failed, no season, or none begun. Shared by How the last ones went and the
 *  Turnstile, so both say the same. */
function NoEventRows({ events }: { events: Load<PlaceEvents | null> }) {
  if (events.state === "loading") return <p className="text-[13px] text-muted-foreground">Reading the last events…</p>;
  if (events.state === "error") return <EmptyLine>{sectionErrorLine("the last events", events.message)}</EmptyLine>;
  const data = events.data;
  if (!data || data.seasons.total === 0) return <EmptyLine>No season to show for this place, so no events show here.</EmptyLine>;
  return <EmptyLine>{noRowsLine(data)}</EmptyLine>;
}

function LastOnes({ events, sample }: { events: Load<PlaceEvents | null>; sample: boolean }) {
  const rows = eventRowsOf(events)?.rows;
  return (
    <Card>
      <SectionHeader icon={History} title="How the last ones went" sample={sample} />
      <CardContent className="pt-3">
        {!rows ? (
          <NoEventRows events={events} />
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {rows.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[14px] font-semibold">{e.title}</span>
                    {e.status && <PillBadge>{e.status}</PillBadge>}
                  </div>
                  <div className="truncate text-[11.5px] text-text-3">
                    {whenLabel(e.startAt)} · {e.season}
                  </div>
                </div>
                <div className="w-[300px] max-w-full text-[12.5px]">
                  <TopThree row={e} />
                </div>
                <Count n={e.came} unit="came" />
                <Count n={e.tables} unit={plural(e.tables ?? 0, "table")} />
              </li>
            ))}
          </ul>
        )}
        <p className="pt-2 text-[11.5px] text-text-3">{lastOnesFooter(events)}</p>
      </CardContent>
    </Card>
  );
}

/** Why Next up is empty. When the read was partial (only some seasons read,
 *  or a season's read stopped at its page cap), it says so rather than
 *  claiming nothing is scheduled. */
export function noNextUpLine(data: NextUp): string {
  if (data.failed > 0) return "The events coming up did not read. Please try again in a moment.";
  if (data.seasons.cut) return "Only some of this place's seasons were read; others may have events coming up.";
  if (data.seasons.capped) return "Only the latest events of each season were read; sooner ones may be coming up.";
  if (data.notShown > 0) return "The events coming up here are not shown to you.";
  return "Nothing scheduled yet.";
}

/** Said when a season's read stopped at its page cap with every event read
 *  still to come: the rows are the soonest of those read, not the soonest. */
const CAPPED_LINE = "A season here has more events coming up than were read, so sooner ones may be missing.";

/** The summary above the schedule: the next event and when ("N+ more" when
 *  every row was filled, so more may come), or why there is none. When the
 *  read was capped, the first row may not be the next one, so it says that. */
export function scheduleSummary(data: NextUp): string {
  const [first, ...rest] = data.rows;
  if (!first) return noNextUpLine(data);
  if (data.seasons.capped) return CAPPED_LINE;
  const more = rest.length > 0 ? ` · ${rest.length}${data.full ? "+" : ""} more coming up` : "";
  return `Next: ${first.title}, ${whenLabel(first.startAt, { zoned: true })}${more}`;
}

/** The line under the Schedule section: where Next up comes from, what is
 *  left out, what comes next, and when it was read. */
export function scheduleFooter(schedule: Load<PlaceSchedule | null>, canSchedule = false): string {
  const data = schedule.state === "ready" ? schedule.data : null;
  const staffRead = data?.templates.state === "ready" || data?.repeats.state === "ready";
  return sectionFooter(schedule, [
    data &&
      (data.nextUp.seasons.total === 0
        ? "Events reach Next up through this place's seasons, and it has none yet."
        : data.nextUp.seasons.cut
          ? "Next up is from the seasons here that start soonest."
          : "Next up is from this place's seasons."),
    data && data.nextUp.rows.length > 0 && data.nextUp.seasons.capped && CAPPED_LINE,
    data && data.nextUp.notShown > 0 && "Some events coming up here are not shown to you, so they are left out.",
    data && data.nextUp.rows.length > 0 && data.nextUp.failed > 0 && "Some events coming up did not read, so they are left out.",
    data?.seasonsCut && staffRead && "Templates and repeats were checked against this place's newest seasons only.",
    !canSchedule && "Making and changing events from here comes next.",
  ]);
}

/** How long a template's events run: "1 h 30 min"; "45 min, the default"
 *  for the backend's default (set so, or never set: the same either way). */
export function runsLabel(ms: number): string {
  return ms === DEFAULT_DURATION_MS ? `${durationLabel(ms)}, the default` : durationLabel(ms);
}

/** A key of a template's JSON as a staff group shows it: its slug (or, not
 *  `slug`, a plain value), or "unread" when the JSON does not say (never guessed). */
function Key({ label, value, slug = true }: { label: string; value: string | null; slug?: boolean }) {
  return (
    <span className="whitespace-nowrap">
      <span className="text-text-3">{label}</span>{" "}
      {value === null ? (
        <span className="text-text-3">unread</span>
      ) : (
        <span className={cn(slug && "font-mono text-[11.5px]")}>{value}</span>
      )}
    </span>
  );
}

/** "Undo" while it lasts; "Undoing…" while it goes; the undo's failure in
 *  plain words beside it. Not shown, `note` says why when there is a reason
 *  to (the undo cannot go now; or it failed and its five minutes ran out). */
function UndoControl({
  undoing,
  shown,
  failed,
  note = null,
  onUndo,
}: {
  undoing: boolean;
  shown: boolean;
  failed: string | null;
  note?: string | null;
  onUndo?: () => void;
}) {
  if (undoing) return <span className="text-xs text-text-3">Undoing…</span>;
  if (!shown) return note ? <span className="text-xs font-semibold text-text-3">{note}</span> : null;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Button variant="subtle" size="sm" onClick={onUndo}>
        Undo
      </Button>
      {failed && <span className="text-xs font-semibold text-failure">{failed}</span>}
    </span>
  );
}

/** "Undo" for the event just made, while it lasts (five minutes). */
function UndoMade({ made, onUndo, now }: { made: MadeEvent; onUndo?: () => void; now: number }) {
  const failed = typeof made.undo === "object" ? made.undo.failed : null;
  const shown = canUndo(made, now);
  return <UndoControl undoing={made.undo === "undoing"} shown={shown} failed={failed} note={!shown && failed ? UNDO_LAPSED_LINE : null} onUndo={onUndo} />;
}

/** "Undo" for the event changed or cancelled here, while it lasts (five
 *  minutes and its own limits: `undoChangeProblem`, said in its place). */
function UndoChange({ changed, onUndo, now }: { changed: ChangedEvent; onUndo?: () => void; now: number }) {
  const failed = typeof changed.undo === "object" && "failed" in changed.undo ? changed.undo.failed : null;
  const shown = canUndoChange(changed, now);
  const open = undoable(changed) && undoWindowOpen(changed, now);
  const note = shown ? null : open ? undoChangeProblem(changed, now) : failed ? UNDO_LAPSED_LINE : null;
  return <UndoControl undoing={changed.undo === "undoing"} shown={shown} failed={failed} note={note} onUndo={onUndo} />;
}

/** The line above Next up for the event changed or cancelled here: a cancel
 *  (its row leaves the list), a change whose row is not among those shown,
 *  and how an undo went; with "Undo" while it lasts. Null when the row itself
 *  says it ("just changed" with its "Undo"), or once the five minutes are over
 *  with nothing more to say (an undo that failed and ran out still says so). */
function ChangedNote({ changed, inList, onUndo, now }: { changed: ChangedEvent; inList: boolean; onUndo?: () => void; now: number }) {
  const settled = changeUndone(changed) || (typeof changed.undo === "object" && "lost" in changed.undo);
  const failed = typeof changed.undo === "object" && "failed" in changed.undo;
  if (changed.kind === "changed" && inList && !settled) return null;
  if (!settled && !failed && changed.undo !== "undoing" && !undoWindowOpen(changed, now)) return null;
  return (
    <div data-changed-line className="flex flex-wrap items-center gap-2 text-xs font-semibold text-text-3">
      {changedLine(changed)}
      {!settled && <UndoChange changed={changed} onUndo={onUndo} now={now} />}
    </div>
  );
}

/** A row's own block under it, the row's full width (nothing when empty). */
function RowBelow({ children }: { children: React.ReactNode }) {
  if (children === null || children === undefined || children === false) return null;
  return <div className="basis-full pt-1">{children}</div>;
}

/** Next up's rows, soonest first. The event just made here (`made`) carries
 *  "Undo" on its row; when it is not among the rows, a line says why (or, while
 *  `rereading`, that Next up is being read again), with "Undo" beside it; once
 *  undone, a line says so. `above` goes first (unit 6c's line for an event
 *  changed or cancelled); `badge`, `side` and `below` add to each row (its
 *  "just changed", its Change and Cancel, its open form). */
function NextUpList({
  data,
  made = null,
  rereading = false,
  onUndo,
  now,
  above,
  badge,
  side,
  below,
}: {
  data: NextUp;
  made?: MadeEvent | null;
  rereading?: boolean | "failed";
  onUndo?: () => void;
  now: number;
  above?: React.ReactNode;
  badge?: (row: NextUpRow) => React.ReactNode;
  side?: (row: NextUpRow) => React.ReactNode;
  below?: (row: NextUpRow) => React.ReactNode;
}) {
  const madeRow = made !== null && !wasUndone(made) && data.rows.some((e) => e.id === made.id);
  return (
    <div className="flex flex-col gap-2">
      {above}
      {data.rows.length === 0 ? (
        <EmptyLine>{noNextUpLine(data)}</EmptyLine>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {data.rows.map((e: NextUpRow) => (
            <li key={e.id} data-made={e.id === made?.id || undefined} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[14px] font-semibold">{e.title}</span>
                  {madeRow && e.id === made.id && <PillBadge>just made</PillBadge>}
                  {badge?.(e)}
                </div>
                <div className="truncate text-[11.5px] text-text-3">
                  {whenLabel(e.startAt, { zoned: true })} · {e.season}
                </div>
              </div>
              {e.preset && <PillBadge>{e.preset}</PillBadge>}
              <div className={cn("text-[13px]", e.unread ? "text-text-3" : "text-foreground")}>
                {e.unread ? "details did not read" : e.game}
              </div>
              {madeRow && e.id === made.id && <UndoMade made={made} onUndo={onUndo} now={now} />}
              {side?.(e)}
              <RowBelow>{below?.(e)}</RowBelow>
            </li>
          ))}
        </ul>
      )}
      {made && wasUndone(made) && <p className="text-xs font-semibold text-text-3">{undoneLine(made)}</p>}
      {/* Kept while the undo goes, so the line it answers never vanishes under it. */}
      {made && !madeRow && !wasUndone(made) && (canUndo(made, now) || made.undo === "undoing") && (
        <div data-made-line className="flex flex-wrap items-center gap-2 text-xs font-semibold text-text-3">
          {rereading === "failed"
            ? `${made.title} is made. Next up did not read again; reopen the page to see it.`
            : rereading
              ? `${made.title} is made. Reading Next up again…`
              : madeNotShownLine(made)}
          <UndoMade made={made} onUndo={onUndo} now={now} />
        </div>
      )}
    </div>
  );
}

/** What a staff-only group says before (or instead of) its rows. */
function StaffGroupState({ group, noun }: { group: StaffGroup<unknown>; noun: string }) {
  if (group.state === "staff-only") return <EmptyLine>Shown to pop.in staff for now.</EmptyLine>;
  if (group.state === "unread" || group.state === "reading") {
    return <p className="text-sm text-muted-foreground">Reading the {noun}…</p>;
  }
  if (group.state === "failed") {
    return <EmptyLine>The {noun} did not read. Close and open the schedule to try again.</EmptyLine>;
  }
  return null;
}

/** The quiet line under a staff group (`one`: "template"): entries whose
 *  JSON did not read (so where they belong is not known). Null when there is
 *  nothing to say. */
export function staffGroupNotes(group: StaffGroup<unknown>, one: string): string | null {
  if (group.state !== "ready" || group.unread === 0) return null;
  const n = group.unread;
  return `${n} ${plural(n, one)} could not be read, so where ${n === 1 ? "it belongs" : "they belong"} is not known.`;
}

function StaffGroupNotes({ group, one }: { group: StaffGroup<unknown>; one: string }) {
  const notes = staffGroupNotes(group, one);
  if (!notes) return null;
  return <p className="pt-2 text-xs font-semibold text-text-3">{notes}</p>;
}

function RepeatList({ group }: { group: StaffGroup<RepeatRow> }) {
  if (group.state !== "ready") return <StaffGroupState group={group} noun="repeats" />;
  return (
    <div>
      {group.rows.length === 0 ? (
        <EmptyLine>No repeats tied to this place.</EmptyLine>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {group.rows.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 py-2.5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">{r.name}</span>
                <span className="text-[12.5px]">{repeatWhenLabel(r)}</span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
                <Key label="game" value={r.game} />
                <Key label="rules" value={r.preset} />
                <Key label="runs" value={durationLabel(r.durationMs)} slug={false} />
                <span className="whitespace-nowrap">
                  <span className="text-text-3">template</span> {r.template}
                </span>
              </div>
              <div className="text-[11.5px] text-text-3">Next one is made at {whenLabel(r.nextMadeAt, { zoned: true })}.</div>
            </li>
          ))}
        </ul>
      )}
      <StaffGroupNotes group={group} one="repeat" />
    </div>
  );
}

function TemplateList({ group }: { group: StaffGroup<TemplateRow> }) {
  if (group.state !== "ready") return <StaffGroupState group={group} noun="templates" />;
  return (
    <div>
      {group.rows.length === 0 ? (
        <EmptyLine>No templates tied to this place.</EmptyLine>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {group.rows.map((t) => (
            <li key={t.id} className="flex flex-col gap-1 py-2.5">
              <div className="truncate text-[14px] font-semibold">{t.name}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
                <Key label="game" value={t.game} />
                <Key label="rules" value={t.preset} />
                <Key label="lobby rules" value={t.lobbyPreset} />
                <Key label="runs" value={runsLabel(t.durationMs)} slug={false} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <StaffGroupNotes group={group} one="template" />
    </div>
  );
}

function ScheduleGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section data-group={title} className="py-3 first:pt-0 last:pb-0">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

/** What the Schedule section schedules an event with: the page's
 *  `useScheduleEvent`, and the place's seasons read (the form's season choice). */
export type ScheduleWrites = Scheduling & { seasons: Load<PlaceSeasonsData | null> };

/** A row's open Change or Cancel (unit 6c), by event id, with the row as it
 *  was when opened (kept under Next up while its form is open: see below). */
export interface RowAction {
  kind: "change" | "cancel";
  eventId: string;
  row?: NextUpRow;
}

/** Next up's rows with the row whose Change or Cancel is open kept among them
 *  (by start) when a read again no longer has it (it was cancelled, or moved
 *  out of the rows shown, perhaps by this very form after a lost answer).
 *  Kept under its own key, its form stays as it was, lost-answer line and
 *  "Close" included, until closed. */
export function rowsWithAction(rows: readonly NextUpRow[], action: RowAction | null): NextUpRow[] {
  if (!action?.row || rows.some((r) => r.id === action.eventId)) return [...rows];
  return [...rows, action.row].sort((a, b) => a.startAt - b.startAt);
}

/** How soon before a row's start the Schedule ticks once a second, so Cancel
 *  is held and "started" said on time (within a second). */
const NEAR_START_MS = 2 * 60 * 1000;

/** The Schedule section. Collapsed by default: the next event only (read up
 *  front, for every viewer); "Show schedule" opens Next up, Repeats and
 *  Templates, asking for the two staff-only reads then (`onShow`: once per
 *  visit, again on reopening after one failed; for anyone but staff it asks
 *  for nothing). With `scheduling` (staff, dev or preview only), "Schedule an
 *  event" on the header opens the form inline (and the section, so the
 *  templates are read); `formOpen`, `formStep` and `now` start it open, at a
 *  step, on a fixed clock (for the proof). With `scheduling`, each row still
 *  to come gets "Change" and "Cancel" (unit 6c): one row's at a time, inline
 *  under it, and none while the schedule form is open (nor that form while
 *  one is); `rowAction` and `rowStep` start one open, at a step (for the
 *  proof). Otherwise read-only. */
function Schedule({
  schedule,
  sample,
  open: initiallyOpen,
  onShow,
  place,
  games,
  scheduling,
  formOpen: initiallyFormOpen = false,
  formStep,
  rowAction: initialRowAction = null,
  rowStep,
  now: fixedNow,
}: {
  schedule: Load<PlaceSchedule | null>;
  sample: boolean;
  open: boolean;
  onShow?: () => void;
  place: PlaceData;
  games: Load<PlaceGames | null>;
  scheduling?: ScheduleWrites;
  formOpen?: boolean;
  formStep?: FormStep;
  rowAction?: RowAction | null;
  rowStep?: FormStep | CancelStep;
  now?: number;
}) {
  const [open, setOpen] = React.useState(initiallyOpen || ((initiallyFormOpen || !!initialRowAction) && !!scheduling));
  const [formOpen, setFormOpen] = React.useState(initiallyFormOpen && !!scheduling);
  const [rowAction, setRowAction] = React.useState<RowAction | null>(scheduling ? initialRowAction : null);
  const made = scheduling?.made ?? null;
  const changed = scheduling?.changed ?? null;
  const data = schedule.state === "ready" ? schedule.data : null;
  const clock = Date.now();
  // Tick only while "Undo" may still be on screen (the tick itself ends it),
  // or a row's start is near (Cancel held, then "started", on time).
  const undoLive = made !== null && !wasUndone(made) && undoWindowOpen(made, clock);
  const changeLive = changed !== null && !changeUndone(changed) && undoWindowOpen(changed, clock);
  const nearStart = !!scheduling && !!data?.nextUp.rows.some((r) => r.startAt - clock > -5_000 && r.startAt - clock <= NEAR_START_MS);
  // The page's one timer, once a second (`use-now.ts`): "Undo" goes within a
  // second of its five minutes. The tick only re-renders; the time read is
  // always the clock's own, so a render after the tick stops never sees a
  // stale "now".
  useNow((undoLive || changeLive || nearStart) && fixedNow === undefined);
  const now = fixedNow ?? clock;
  const askOptions = scheduling?.askOptions;
  const changing = rowAction?.kind === "change";
  React.useEffect(() => {
    if (formOpen || changing) askOptions?.();
  }, [formOpen, changing, askOptions]);
  // The event is read each time a row's Change or Cancel opens, asked in the
  // same press, so the first frame already says "Reading the event…" (never a
  // snapshot from an earlier open).
  const openRow = (action: RowAction) => {
    setRowAction(action);
    scheduling?.askEvent(action.eventId);
  };
  // Started open on a row (`rowAction`): asked for once, on mount.
  const askAtMount = React.useRef(scheduling && initialRowAction ? () => scheduling.askEvent(initialRowAction.eventId) : null);
  React.useEffect(() => askAtMount.current?.(), []);
  // Open with the staff groups not read yet (opened, or opened before the
  // first read answered): ask for them. Opening again after a staff read
  // failed asks again.
  const unread = data?.templates.state === "unread";
  const failed = data?.templates.state === "failed" || data?.repeats.state === "failed";
  React.useEffect(() => {
    if (open && unread) onShow?.();
  }, [open, unread, onShow]);
  const toggle = () => {
    if (!open && failed) onShow?.();
    setOpen(!open);
  };
  const untied = data ? untiedLine(data) : null;
  const scheduleButton = scheduling && !formOpen && !rowAction && (
    <Button
      variant="subtle"
      size="sm"
      // After a lost answer, wait until Next up has been read again.
      disabled={scheduling.checking}
      onClick={() => {
        setFormOpen(true);
        setOpen(true);
      }}
    >
      <CalendarPlus /> {scheduling.checking ? "Reading Next up again…" : "Schedule an event"}
    </Button>
  );
  /** Each row's Change and Cancel, its "just changed" with "Undo", its open
   *  form; and the line above for an event changed or cancelled here. */
  const rowWrites = (nextUp: NextUp): Pick<React.ComponentProps<typeof NextUpList>, "above" | "badge" | "side" | "below"> => {
    if (!scheduling) return {};
    const close = () => setRowAction(null);
    const mine = (row: NextUpRow) => (changed?.id === row.id && !changeUndone(changed) ? changed : null);
    return {
      above: changed && (
        <ChangedNote changed={changed} inList={nextUp.rows.some((r) => r.id === changed.id)} onUndo={scheduling.undoChange} now={now} />
      ),
      badge: (row) => {
        const c = mine(row);
        if (c) return <PillBadge>{c.kind === "changed" ? "just changed" : "cancelled"}</PillBadge>;
        // Kept while its form is open (`rowsWithAction`).
        if (!nextUp.rows.some((r) => r.id === row.id)) return <PillBadge>no longer under Next up</PillBadge>;
        return null;
      },
      side: (row) => {
        const c = mine(row);
        // Cancelled here: the row leaves with the next read; nothing to press.
        if (c?.kind === "cancelled") return null;
        const write = rowWriteOf(row.startAt, now);
        const undo = c && <UndoChange changed={c} onUndo={scheduling.undoChange} now={now} />;
        if (write === "started") {
          // Its undo cannot go once started: "Undoing…" while one goes, an undo
          // that failed said, then the started line, once.
          const failedUndo = !!c && typeof c.undo === "object" && "failed" in c.undo;
          return (
            <span className="flex flex-wrap items-center gap-2">
              {c?.undo === "undoing" && undo}
              {failedUndo && <span className="text-xs font-semibold text-text-3">{UNDO_LAPSED_LINE}</span>}
              <span className="text-xs text-text-3">{STARTED_LINE}</span>
            </span>
          );
        }
        // One at a time: none while a form is open (its own row shows the
        // form), nor while an undo goes (its answer must land first).
        if (formOpen || rowAction || changed?.undo === "undoing" || made?.undo === "undoing") return undo;
        return (
          <span className="flex flex-wrap items-center gap-2">
            {undo}
            <Button variant="subtle" size="sm" onClick={() => openRow({ kind: "change", eventId: row.id, row })}>
              Change
            </Button>
            <Button
              variant="subtle"
              size="sm"
              disabled={write === "too-close"}
              onClick={() => openRow({ kind: "cancel", eventId: row.id, row })}
            >
              Cancel
            </Button>
            {write === "too-close" && <span className="text-xs text-text-3">{TOO_CLOSE_LINE}</span>}
          </span>
        );
      },
      below: (row) => {
        if (rowAction?.eventId !== row.id) return null;
        if (rowAction.kind === "cancel") {
          return (
            <CancelEventReview
              scheduling={scheduling}
              eventId={row.id}
              onClose={close}
              now={fixedNow}
              step={rowStep === "review" || rowStep === "sending" || rowStep === "lost" ? rowStep : undefined}
            />
          );
        }
        return (
          <ScheduleEventForm
            place={place}
            scheduling={scheduling}
            games={games}
            seasons={scheduling.seasons}
            templates={data?.templates ?? { state: "unread" }}
            onClose={close}
            changing={row.id}
            now={fixedNow}
            step={rowStep === "edit" || rowStep === "review" || rowStep === "creating" || rowStep === "lost" ? rowStep : undefined}
          />
        );
      },
    };
  };
  return (
    <Card>
      <SectionHeader icon={CalendarClock} title="Schedule" sample={sample} action={scheduleButton} />
      <CardContent className="pt-3">
        {scheduling && formOpen && (
          <div className="pb-3">
            <ScheduleEventForm
              place={place}
              scheduling={scheduling}
              games={games}
              seasons={scheduling.seasons}
              templates={data?.templates ?? { state: "unread" }}
              onClose={() => setFormOpen(false)}
              now={fixedNow}
              step={formStep}
            />
          </div>
        )}
        {schedule.state === "loading" && <p className="text-sm text-muted-foreground">Reading the schedule…</p>}
        {schedule.state === "error" && <EmptyLine>{sectionErrorLine("the schedule", schedule.message)}</EmptyLine>}
        {schedule.state === "ready" &&
          (!data ? (
            <EmptyLine>Nothing scheduled yet.</EmptyLine>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold">{scheduleSummary(data.nextUp)}</span>
                <Button variant="subtle" size="sm" aria-expanded={open} onClick={toggle}>
                  {open ? "Hide schedule" : "Show schedule"}
                  <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
                </Button>
              </div>
              {open && (
                <div className="flex flex-col divide-y divide-border">
                  <ScheduleGroup title="Next up">
                    <NextUpList
                      data={{ ...data.nextUp, rows: rowsWithAction(data.nextUp.rows, rowAction) }}
                      made={made}
                      {...(scheduling && rowWrites(data.nextUp))}
                      // Read before the event was made: the page is reading it again (samples are not re-read), or that failed.
                      rereading={
                        made !== null && schedule.state === "ready" && schedule.readAt !== null && schedule.readAt < made.madeAt
                          ? schedule.refreshFailed
                            ? "failed"
                            : true
                          : false
                      }
                      onUndo={scheduling?.undo}
                      now={now}
                    />
                  </ScheduleGroup>
                  <ScheduleGroup title="Repeats">
                    <RepeatList group={data.repeats} />
                  </ScheduleGroup>
                  <ScheduleGroup title="Templates">
                    <TemplateList group={data.templates} />
                    {untied && <p className="pt-2 text-xs text-text-3">{untied}</p>}
                  </ScheduleGroup>
                </div>
              )}
            </div>
          ))}
        <p className="pt-2 text-xs text-text-3">{scheduleFooter(schedule, !!scheduling)}</p>
      </CardContent>
    </Card>
  );
}

/** "N members", "1 member". */
export function membersLabel(n: number): string {
  return `${n.toLocaleString()} ${plural(n, "member")}`;
}

/** The line under the People section: which seasons the members are from,
 *  what is left out, what a member is today, and when it was read. */
export function membersFooter(members: Load<PlaceMembers | null>): string {
  const data = members.state === "ready" ? members.data : null;
  const read = data?.seasons.cut ? data.seasons.read : 0;
  return sectionFooter(members, [
    read > 0 &&
      (read === 1 ? "From the season here that began most recently." : `From the ${read} seasons here that began most recently.`),
    data && data.notShown > 0 && "Some seasons' members are not shown to you.",
    "Members are people who joined or played a scored event in one of this place's seasons. Leaving, regulars and visits come later.",
  ]);
}

/** One season's members: faces with names, then "+N more"; or why there are none. */
function MemberList({ group }: { group: SeasonMembers }) {
  if (group.state === "hidden") return <EmptyLine>Members not shown to you.</EmptyLine>;
  if (group.state === "failed" || group.listFailed) {
    return <EmptyLine>This season's members did not read. Please try again in a moment.</EmptyLine>;
  }
  if (group.total === 0 || group.faces?.length === 0) return <EmptyLine>No one has joined this season yet.</EmptyLine>;
  if (!group.faces) return null;
  return (
    <div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 md:grid-cols-4">
        {group.faces.map((f) => (
          <li key={f.id} className="flex min-w-0 items-center gap-2 text-sm">
            <FaceAvatar face={f} />
            <span className="truncate">{f.name}</span>
          </li>
        ))}
      </ul>
      {group.more > 0 && <p className="pt-2 text-xs font-semibold text-text-3">+{group.more.toLocaleString()} more</p>}
    </div>
  );
}

/** The summary above the members: the count, "N+" when it is a floor. */
export function membersSummary(data: PlaceMembers): string {
  return `${data.count.toLocaleString()}${data.atLeast ? "+" : ""} ${plural(data.count, "member")}`;
}

/** The members, by season (someone following two shows under both). */
function MemberGroups({ data }: { data: PlaceMembers }) {
  if (data.lists === "reading") return <p className="text-sm text-muted-foreground">Reading the members…</p>;
  if (data.groups.length === 1) return <MemberList group={data.groups[0]!} />;
  return (
    <div className="flex flex-col divide-y divide-border">
      {data.groups.map((g) => (
        <section key={g.id} className="py-3 first:pt-0 last:pb-0">
          <h3 className="mb-2 flex items-baseline gap-2 text-sm font-semibold">
            <span className="truncate">{g.title}</span>
            {g.total !== null && <span className="shrink-0 text-xs font-normal text-text-3">{membersLabel(g.total)}</span>}
          </h3>
          <MemberList group={g} />
        </section>
      ))}
    </div>
  );
}

/** The People section. Collapsed by default: the count only (read cheaply);
 *  "Show members" opens the lists, read once per visit (`onShow`). */
function People({
  members,
  sample,
  open: initiallyOpen,
  onShow,
}: {
  members: Load<PlaceMembers | null>;
  sample: boolean;
  open: boolean;
  onShow?: () => void;
}) {
  const [open, setOpen] = React.useState(initiallyOpen);
  const data = members.state === "ready" ? members.data : null;
  const toggle = () => {
    if (!open) onShow?.();
    setOpen(!open);
  };
  return (
    <Card>
      <SectionHeader icon={Users} title="People" sample={sample} />
      <CardContent className="pt-3">
        {members.state === "loading" && <p className="text-sm text-muted-foreground">Reading the members…</p>}
        {members.state === "error" && <EmptyLine>{sectionErrorLine("the members", members.message)}</EmptyLine>}
        {members.state === "ready" &&
          (!data || data.seasons.total === 0 ? (
            <EmptyLine>No season here yet, so no members show.</EmptyLine>
          ) : data.groups.length === 0 ? (
            <EmptyLine>No season here has begun yet, so no members are counted yet.</EmptyLine>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold">{membersSummary(data)}</span>
                <Button variant="subtle" size="sm" aria-expanded={open} onClick={toggle}>
                  {open ? "Hide members" : "Show members"}
                  <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
                </Button>
              </div>
              {open && <MemberGroups data={data} />}
            </div>
          ))}
        <p className="pt-2 text-xs text-text-3">{membersFooter(members)}</p>
      </CardContent>
    </Card>
  );
}

/** A score as the backend gives it, in points: "1,840 points". */
export function pointsLabel(n: number): string {
  return `${n.toLocaleString()} ${plural(n, "point")}`;
}

/** The line under the Standings section: which seasons, what is left out,
 *  that standings are per season, and when they were read. */
export function standingsFooter(standings: Load<PlaceStandings | null>): string {
  const data = standings.state === "ready" ? standings.data : null;
  const read = data?.seasons.cut ? data.seasons.read : 0;
  return sectionFooter(standings, [
    read > 0 && (read === 1 ? "From the most recent season here." : `From the ${read} most recent seasons here.`),
    data && data.notShown > 0 && "Some seasons' standings are not shown to you.",
    "Standings are per season. A place-wide roll-up comes later.",
  ]);
}

/** Why no season has a table. When only the most recent seasons were read
 *  (older ones, unlisted, may have begun), it says so rather than claiming
 *  nothing has begun, as {@link noRowsLine} does for the last events. */
export function noStandingsLine(data: PlaceStandings): string {
  if (data.seasons.cut) return "Only the most recent seasons were read, and none of them has begun; older ones may have standings.";
  return "No season here has begun yet, so no one is ranked yet.";
}

/** One season's table: rank, face, name and points, best first; or why there is none. */
function StandingList({ group }: { group: SeasonStandings }) {
  if (group.state === "unread") return <p className="text-sm text-muted-foreground">Reading this season's standings…</p>;
  if (group.state === "hidden") return <EmptyLine>Standings not shown to you.</EmptyLine>;
  if (group.state === "failed") return <EmptyLine>This season's standings did not read. Please try again in a moment.</EmptyLine>;
  if (group.rows.length === 0 && !group.more) return <EmptyLine>No one has scored in this season yet.</EmptyLine>;
  return (
    <div>
      {group.firstMissing && (
        <p className="pb-2 text-xs font-semibold text-text-3">First place did not load.</p>
      )}
      <ol className="flex flex-col divide-y divide-border">
        {group.rows.map((r) => (
          <li key={r.face.id} className="flex items-center gap-3 py-2">
            <span className="w-6 shrink-0 text-right font-mono text-sm tabular-nums text-text-3">{r.rank}</span>
            <FaceAvatar face={r.face} />
            <span className="min-w-0 flex-1 truncate text-sm">{r.face.name}</span>
            <span className="shrink-0 font-mono text-sm tabular-nums">
              {r.score.toLocaleString()} <span className="text-text-3">{plural(r.score, "point")}</span>
            </span>
          </li>
        ))}
      </ol>
      {group.more && (
        <p className="pt-2 text-xs font-semibold text-text-3">More have scored; only the top {STANDINGS_SHOWN} show.</p>
      )}
    </div>
  );
}

/** The summary above the tables: who leads the season running most recently
 *  (or finished first in it, once it is over). */
export function standingsSummary(data: PlaceStandings): string | null {
  const leader = leaderOf(data);
  if (!leader) return null;
  const verb = leader.ended ? "finished first in" : "leads";
  return `${leader.face.name} ${verb} ${leader.season} with ${pointsLabel(leader.score)}.`;
}

/** The Standings section. Collapsed by default: who leads only (the first
 *  season's table, read up front); "Show standings" opens one table per season,
 *  asking for the others then (`onShow`, once per visit). */
function Standings({
  standings,
  sample,
  open: initiallyOpen,
  onShow,
}: {
  standings: Load<PlaceStandings | null>;
  sample: boolean;
  open: boolean;
  onShow?: () => void;
}) {
  const [open, setOpen] = React.useState(initiallyOpen);
  const data = standings.state === "ready" ? standings.data : null;
  const summary = data ? standingsSummary(data) : null;
  // Open with tables not read yet (opened, or opened before the first read
  // answered): ask for them.
  const unread = data?.tables === "unread";
  React.useEffect(() => {
    if (open && unread) onShow?.();
  }, [open, unread, onShow]);
  return (
    <Card>
      <SectionHeader icon={Trophy} title="Standings" sample={sample} />
      <CardContent className="pt-3">
        {standings.state === "loading" && <p className="text-sm text-muted-foreground">Reading the standings…</p>}
        {standings.state === "error" && <EmptyLine>{sectionErrorLine("the standings", standings.message)}</EmptyLine>}
        {standings.state === "ready" &&
          (!data || data.seasons.total === 0 ? (
            <EmptyLine>No season here yet, so no standings show.</EmptyLine>
          ) : data.groups.length === 0 ? (
            <EmptyLine>{noStandingsLine(data)}</EmptyLine>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                {summary && <span className="text-sm font-semibold">{summary}</span>}
                <Button variant="subtle" size="sm" aria-expanded={open} onClick={() => setOpen(!open)}>
                  {open ? "Hide standings" : "Show standings"}
                  <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
                </Button>
              </div>
              {open && (
                <div className="flex flex-col divide-y divide-border">
                  {data.groups.map((g) => (
                    <section key={g.id} className="py-3 first:pt-0 last:pb-0">
                      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
                        <span className="truncate">{g.title}</span>
                        {g.ended && <PillBadge>ended</PillBadge>}
                      </h3>
                      <StandingList group={g} />
                    </section>
                  ))}
                </div>
              )}
            </div>
          ))}
        <p className="pt-2 text-xs text-text-3">{standingsFooter(standings)}</p>
      </CardContent>
    </Card>
  );
}

/** What a listed slug with no catalog entry is called ({@link unknownWords}). */
export function unknownLabel(data: PlaceGames): string {
  return unknownWords(data.enabledOnly);
}

/** "+1 not an active catalog game · 1 disabled": what the games count leaves out;
 *  null when it leaves nothing out. */
export function gamesAside(data: PlaceGames): string | null {
  const parts = [data.unknown > 0 && `+${data.unknown} ${unknownLabel(data)}`, data.disabled > 0 && `${data.disabled} disabled`];
  return parts.filter((p): p is string => !!p).join(" · ") || null;
}

/** The summary above the games: how many the place can run, and what the
 *  count leaves out. */
export function gamesSummary(data: PlaceGames): string {
  const games = `${data.count.toLocaleString()} ${plural(data.count, "game")}`;
  if (data.kind === "house") return `Runs every enabled game: ${games}.`;
  if (data.rows.length === 0) return "The brand lists no games, so none show.";
  const aside = gamesAside(data);
  return aside ? `Runs ${games} (${aside}).` : `Runs ${games}.`;
}

/** The line under the Games section: what the list cannot say, that changing
 *  it comes later, and when it was read. To those who can change the brand's
 *  list (`editable`: its owner and members, and staff; unit 8), a brand's
 *  place says where instead. */
export function gamesFooter(games: Load<PlaceGames | null>, editable = false): string {
  const data = games.state === "ready" ? games.data : null;
  return sectionFooter(games, [
    data && "Per-game feature flags are unread.",
    data?.kind === "brand" && editable
      ? "The brand's Games page changes this list for every place of the brand; a place's own list comes later."
      : "Changing which games a place runs comes later.",
  ]);
}

/** One game the rooms play now, with the rooms playing it. */
export interface PlayingNow {
  game: string;
  rooms: string[];
}

/** What the rooms play right now, from the room-now read (no read of its
 *  own): each game once, with its rooms, the main room's first; how many
 *  rooms have no game on; and how many open tables were not read. */
export function playingNowOf(place: PlaceData): { games: PlayingNow[]; idle: number; unread: number } {
  // Keyed by the game's slug, never its name: two games may share a name.
  const byGame = new Map<string, PlayingNow>();
  let idle = 0;
  for (const r of roomsOf(place)) {
    if (r.activity === null || r.activityKey === null) idle++;
    else {
      const playing = byGame.get(r.activityKey);
      if (playing) playing.rooms.push(r.title);
      else byGame.set(r.activityKey, { game: r.activity, rooms: [r.title] });
    }
  }
  return {
    games: [...byGame.values()],
    idle,
    unread: tablesNotRead(place),
  };
}

/** What a room starts on: the game and its engine, and whether the place
 *  pins it or takes the platform default. */
function StartsOnRow({ label, start }: { label: string; start: StartsOn }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <span className="w-24 shrink-0 text-xs text-text-3">{label}</span>
      {start.game ? (
        <>
          <span className="text-sm font-semibold">{start.game.name}</span>
          <span className="text-xs text-muted-foreground">{start.game.engine}</span>
          {!start.game.enabled && <PillBadge>disabled</PillBadge>}
        </>
      ) : (
        <span className="text-sm text-text-3">No game resolves.</span>
      )}
      <span className="ml-auto text-xs text-text-3">{start.pinned ? "set for this place" : "platform default"}</span>
    </li>
  );
}

/** The games, one per line: name, slug and engine; a game switched off
 *  greyed and marked; a slug that names no game, `unknown` ("not in the
 *  catalog"). */
function GameList({ rows, unknown }: { rows: GameRow[]; unknown: string }) {
  return (
    <ul className="flex flex-col divide-y divide-border">
      {rows.map((r, i) => (
        <li
          key={`${r.slug}-${i}`}
          className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 py-2", r.game && !r.game.enabled && "opacity-60")}
        >
          {r.game ? (
            <>
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="truncate text-sm font-semibold">{r.game.name}</span>
                {!r.game.enabled && <PillBadge>disabled</PillBadge>}
              </span>
              <span className="font-mono text-xs text-text-3">{r.game.slug}</span>
              <span className="w-20 text-right text-xs text-muted-foreground">{r.game.engine}</span>
            </>
          ) : (
            <>
              <span className="min-w-0 flex-1 truncate font-mono text-sm text-text-3">{r.slug}</span>
              <span className="text-xs text-text-3">{unknown}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/** The three groups: what the rooms start on, the games the place runs, and
 *  what is playing now. */
function GameGroups({ data, place }: { data: PlaceGames; place: PlaceData }) {
  const now = playingNowOf(place);
  return (
    <div className="flex flex-col divide-y divide-border">
      <section className="py-3 first:pt-0">
        <h3 className="mb-1 text-sm font-semibold">Starts on</h3>
        <ul className="flex flex-col divide-y divide-border">
          <StartsOnRow label="Main room" start={data.startsOn.main} />
          <StartsOnRow label="Tables" start={data.startsOn.tables} />
        </ul>
      </section>
      <section className="py-3">
        <h3 className="mb-1 text-sm font-semibold">This place runs</h3>
        {data.kind === "house" && <p className="pb-1 text-sm text-muted-foreground">Every enabled game.</p>}
        {data.rows.length === 0 ? (
          <EmptyLine>
            {data.kind === "house" ? "The catalog lists no games." : "The brand lists no games. An empty list shows no games."}
          </EmptyLine>
        ) : (
          <GameList rows={data.rows} unknown={unknownLabel(data)} />
        )}
      </section>
      <section className="py-3 last:pb-0">
        <h3 className="mb-1 text-sm font-semibold">Playing now</h3>
        {now.games.length === 0 ? (
          <EmptyLine>No room has a game on right now.</EmptyLine>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {now.games.map((g) => (
              <li key={g.game} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="text-sm font-semibold">{g.game}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-text-3">{g.rooms.join(", ")}</span>
              </li>
            ))}
          </ul>
        )}
        {now.idle > 0 && now.games.length > 0 && (
          <p className="pt-1 text-xs text-text-3">
            {now.idle} {plural(now.idle, "room")} with no game on.
          </p>
        )}
        {now.unread > 0 && <p className="pt-1 text-xs text-text-3">{tablesNotReadLine(now.unread)}</p>}
      </section>
    </div>
  );
}

/** The Games section. Collapsed by default: how many games the place can
 *  run (read up front for the card); "Show games" opens the three groups,
 *  from the same read and the room-now read (nothing more is read). */
function Games({
  games,
  place,
  sample,
  open: initiallyOpen,
  editable,
}: {
  games: Load<PlaceGames | null>;
  place: PlaceData;
  sample: boolean;
  open: boolean;
  editable: boolean;
}) {
  const [open, setOpen] = React.useState(initiallyOpen);
  const data = games.state === "ready" ? games.data : null;
  return (
    <Card>
      <SectionHeader icon={Gamepad2} title="Games" sample={sample} />
      <CardContent className="pt-3">
        {games.state === "loading" && <p className="text-sm text-muted-foreground">Reading the games…</p>}
        {games.state === "error" && <EmptyLine>{sectionErrorLine("the games", games.message)}</EmptyLine>}
        {games.state === "ready" &&
          (!data ? (
            <EmptyLine>No games to show for this place.</EmptyLine>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold">{gamesSummary(data)}</span>
                <Button variant="subtle" size="sm" aria-expanded={open} onClick={() => setOpen(!open)}>
                  {open ? "Hide games" : "Show games"}
                  <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
                </Button>
              </div>
              {open && <GameGroups data={data} place={place} />}
            </div>
          ))}
        <p className="pt-2 text-xs text-text-3">{gamesFooter(games, editable)}</p>
      </CardContent>
    </Card>
  );
}

/** One game the rooms play now, as a number: how many rooms play it. */
export interface PlayingCount {
  game: string;
  rooms: number;
}

/** The Turnstile's right-now numbers. */
export interface RightNowNumbers {
  /** Who is here, as shown ("N+" when some open tables were not read). */
  here: string;
  tablesOpen: number;
  playing: PlayingCount[];
  idle: number;
  unread: number;
}

/** The Turnstile's right-now numbers, from the room-now read alone: who is
 *  here ("N+" when some open tables were not read), every open table (read or
 *  not), what the rooms play (each game with its room count, the main room's
 *  first) and how many rooms have no game on. */
export function rightNowOf(place: PlaceData): RightNowNumbers {
  const now = playingNowOf(place);
  return {
    here: headcountLabel(place),
    tablesOpen: place.subGatherings.totalCount,
    playing: now.games.map((g) => ({ game: g.game, rooms: g.rooms.length })),
    idle: now.idle,
    unread: now.unread,
  };
}

/** A label, its number, and the number's unit (a Right now line). */
function NumberLine({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <li className="flex items-center gap-3 py-2">
      <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
      <span className="shrink-0 font-mono text-sm tabular-nums">
        {value}
        {unit && <span className="text-text-3"> {unit}</span>}
      </span>
    </li>
  );
}

/** The last events' visitors and tables, from the last-events read; or why
 *  there are none. A dash for who came says why on its row. */
function TurnstileEvents({ events }: { events: Load<PlaceEvents | null> }) {
  const read = eventRowsOf(events);
  if (!read) return <NoEventRows events={events} />;
  const { data, rows } = read;
  return (
    <div>
      <ul className="flex flex-col divide-y divide-border">
        {rows.map((e) => {
          const note = cameNote(e);
          return (
            <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-semibold">{e.title}</span>
                  {e.status && <PillBadge>{e.status}</PillBadge>}
                </div>
                <div className="truncate text-xs text-text-3">
                  {whenLabel(e.startAt)}
                  {note && ` · ${note}`}
                </div>
              </div>
              <Count n={e.came} unit="came" />
              <Count n={e.tables} unit={plural(e.tables ?? 0, "table")} />
            </li>
          );
        })}
      </ul>
      {data.seasons.cut && data.seasons.read > 0 && (
        <p className="pt-1 text-xs text-text-3">From the {data.seasons.read} most recent seasons here.</p>
      )}
      {data.notShown > 0 && <p className="pt-1 text-xs text-text-3">Some events here are not shown to you, so they are left out.</p>}
    </div>
  );
}

/** The Turnstile's live numbers, with when they were read (as the room now
 *  says it, so the two never disagree). */
function RightNow({ now, freshness }: { now: RightNowNumbers; freshness?: Freshness }) {
  return (
    <section className="py-3 first:pt-0">
      <h3 className="mb-1 text-sm font-semibold">{withSource("Right now", freshness)}</h3>
      <ul className="flex flex-col divide-y divide-border">
        <NumberLine label="Here now" value={now.here} />
        <NumberLine label="Tables open" value={now.tablesOpen.toLocaleString()} />
        {/* Two games may share a name: keyed by position, never by name. */}
        {now.playing.map((g, i) => (
          <NumberLine key={`${i}-${g.game}`} label={`Playing ${g.game}`} value={g.rooms.toLocaleString()} unit={plural(g.rooms, "room")} />
        ))}
        {now.idle > 0 && <NumberLine label="No game on" value={now.idle.toLocaleString()} unit={plural(now.idle, "room")} />}
      </ul>
      {now.unread > 0 && <p className="pt-1 text-xs text-text-3">{tablesNotReadLine(now.unread)}</p>}
      {freshness && <UpdatedLine {...freshness} />}
    </section>
  );
}

/** The Turnstile section. Collapsed by default: who is here now; "Show the
 *  numbers" opens the four groups, from the room-now and last-events reads
 *  (nothing more is read). */
function Turnstile({
  place,
  events,
  sample,
  freshness,
  open: initiallyOpen,
}: {
  place: PlaceData;
  events: Load<PlaceEvents | null>;
  sample: boolean;
  freshness?: Freshness;
  open: boolean;
}) {
  const [open, setOpen] = React.useState(initiallyOpen);
  const now = rightNowOf(place);
  return (
    <Card>
      <SectionHeader icon={DoorOpen} title="Turnstile" sample={sample} />
      <CardContent className="pt-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold">{withSource(`${now.here} here now`, freshness)}</span>
            <Button variant="subtle" size="sm" aria-expanded={open} onClick={() => setOpen(!open)}>
              {open ? "Hide the numbers" : "Show the numbers"}
              <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
            </Button>
          </div>
          {open && (
            <div className="flex flex-col divide-y divide-border">
              <RightNow now={now} freshness={freshness} />
              <section className="py-3">
                <h3 className="mb-1 text-sm font-semibold">Visitors · all time</h3>
                <EmptyLine>{allTimeLine(place)}</EmptyLine>
              </section>
              <section className="py-3">
                <h3 className="mb-1 text-sm font-semibold">{lastEventsTitle(events)}</h3>
                <TurnstileEvents events={events} />
              </section>
              <section className="py-3 last:pb-0">
                <h3 className="mb-1 text-sm font-semibold">What does not exist yet</h3>
                <ul className="flex flex-col divide-y divide-border">
                  {TURNSTILE_LATER.map((l) => (
                    <li key={l} className="flex items-center gap-3 py-2">
                      <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{l}</span>
                      <span className="text-xs font-semibold text-text-3">later</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </div>
        <p className="pt-2 text-xs text-text-3">{turnstileFooter(freshness)}</p>
      </CardContent>
    </Card>
  );
}

interface Part {
  title: string;
  icon: LucideIcon;
  /** One number where a read exists today; null shows `note`. */
  value: number | null;
  /** What shows in place of a number: "later" unless said otherwise. */
  note?: string;
  /** Shown as "N+": a floor, because not every table was read. */
  atLeast?: boolean;
  /** Someone shown in place of a number (the Leaderboard card's leader). */
  leader?: Leader;
  unit?: string;
  /** A line under the number, or under the note when there is no number. */
  sub?: string;
  dashed?: boolean;
}

/** The Events card: events begun in the last 30 days, read from the place's
 *  seasons ("N+" when some are not shown to this viewer or were not read).
 *  "later" when the place has no season (its events do not reach it here yet)
 *  or the read failed; "reading…" until the read answers. */
function eventsPart(events: Load<PlaceEvents | null>): Part {
  const part = { title: "Events", icon: CalendarDays };
  if (events.state === "loading") return { ...part, value: null, note: "reading…" };
  if (events.state === "error" || !events.data || events.data.seasons.total === 0) return { ...part, value: null };
  const { count, atLeast } = events.data.lately;
  const unit = `${plural(count, "event")}, last ${LATELY_DAYS} days`;
  return { ...part, value: count, atLeast, unit: events.data.notShown > 0 ? `${unit}; some not shown to you` : unit };
}

/** The People card: the place's members, each once across the seasons read
 *  ("N+" when that is a floor). "later" when the place has no season or none
 *  has begun; "did not read" when the read failed (as the section says);
 *  "not shown to you" when no season's members are; "reading…" until the read
 *  answers. Never the live headcount: that is the Turnstile's "here now" line. */
function peoplePart(members: Load<PlaceMembers | null>): Part {
  const part = { title: "People", icon: Users };
  if (members.state === "loading") return { ...part, value: null, note: "reading…" };
  if (members.state === "error") return { ...part, value: null, note: "did not read" };
  if (!members.data) return { ...part, value: null };
  const { count, atLeast, groups, notShown } = members.data;
  if (!groups.some((g) => g.state === "shown")) {
    if (notShown > 0) return { ...part, value: null, note: "not shown to you" };
    return { ...part, value: null, note: groups.length > 0 ? "did not read" : undefined };
  }
  const unit = plural(count, "member");
  return { ...part, value: count, atLeast, unit: notShown > 0 ? `${unit}; some not shown to you` : unit };
}

/** The Leaderboard card: the leader of the season running most recently (face,
 *  name, points). "later" when the place has no season or none has begun;
 *  "older seasons not read" when none read has begun but older ones were not
 *  read; "no scores yet" when that season has no one ranked; "leader not
 *  loaded" when people scored but first place did not come back; "not shown to
 *  you" or "did not read" for that season as the section says; "reading…"
 *  until the read answers. */
function leaderboardPart(standings: Load<PlaceStandings | null>): Part {
  const part = { title: "Leaderboard", icon: Trophy, value: null };
  if (standings.state === "loading") return { ...part, note: "reading…" };
  if (standings.state === "error") return { ...part, note: "did not read" };
  const data = standings.data;
  const first = data?.groups[0];
  if (!first) return data?.seasons.cut ? { ...part, note: "older seasons not read" } : part;
  if (first.state === "hidden") return { ...part, note: "not shown to you" };
  if (first.state !== "shown") return { ...part, note: "did not read" };
  const leader = leaderOf(data!);
  if (leader) return { ...part, leader };
  return { ...part, note: first.firstMissing ? "leader not loaded" : "no scores yet" };
}

/** The Games card: the games the place can run (its brand's list joined to
 *  the catalog; a house place: every enabled game), with what the count
 *  leaves out beneath ("+N not an active catalog game"). "did not read" when the read
 *  failed; "reading…" until it answers. */
function gamesPart(games: Load<PlaceGames | null>): Part {
  const part = { title: "Games", icon: Gamepad2 };
  if (games.state === "loading") return { ...part, value: null, note: "reading…" };
  if (games.state === "error") return { ...part, value: null, note: "did not read" };
  if (!games.data) return { ...part, value: null };
  const { kind, count } = games.data;
  const unit = kind === "house" ? `${plural(count, "game")}, every enabled one` : plural(count, "game");
  return { ...part, value: count, unit, sub: gamesAside(games.data) ?? undefined };
}

/** The Turnstile card: who is here now (live, or the last good read when
 *  the latest failed; "N+" when some open tables were not read), and, on its
 *  own line, that no all-time visitor count is kept (see {@link allTimeLine};
 *  a closed main room's count covers its last 24 hours, not all time). */
export function turnstilePart(place: PlaceData, freshness?: Freshness): Part {
  return {
    title: "Turnstile",
    icon: DoorOpen,
    value: headcountOf(place),
    atLeast: isHeadcountPartial(place),
    unit: withSource("here now", freshness),
    sub: "visitors, all time: not kept yet",
  };
}

/** The parts of a place (the shelf). Numbers only where a read exists today.
 *  `freshness`: when the live numbers were read (live reads only). */
export function partsOf(
  place: PlaceData,
  events: Load<PlaceEvents | null>,
  members: Load<PlaceMembers | null>,
  standings: Load<PlaceStandings | null>,
  games: Load<PlaceGames | null>,
  freshness?: Freshness,
): Part[] {
  return [
    { title: "Tables and rules", icon: LayoutPanelTop, value: place.subGatherings.totalCount, unit: "tables open" },
    gamesPart(games),
    peoplePart(members),
    eventsPart(events),
    leaderboardPart(standings),
    turnstilePart(place, freshness),
    { title: "Look and scene", icon: Palette, value: null },
    { title: "Store", icon: ShoppingBag, value: null, dashed: true },
  ];
}

function Parts({
  place,
  events,
  members,
  standings,
  games,
  sample,
  freshness,
}: {
  place: PlaceData;
  events: Load<PlaceEvents | null>;
  members: Load<PlaceMembers | null>;
  standings: Load<PlaceStandings | null>;
  games: Load<PlaceGames | null>;
  sample: boolean;
  freshness?: Freshness;
}) {
  return (
    <section data-section="The parts">
      <div className="mb-3 flex items-center gap-2 px-0.5">
        <h2 className="text-[13px] font-bold tracking-[.02em]">The parts</h2>
        {sample && <SampleChip />}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {partsOf(place, events, members, standings, games, freshness).map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.title}
              className={cn(
                "rounded-2xl border bg-card p-4",
                p.dashed ? "border-dashed border-border-strong bg-transparent" : "border-border shadow-portal",
              )}
            >
              <div className="flex items-center gap-2 text-[12.5px] font-semibold text-muted-foreground">
                <Icon className="size-4 text-brand" /> {p.title}
              </div>
              {p.leader ? (
                <div className="mt-2.5 flex min-w-0 items-center gap-2">
                  <FaceAvatar face={p.leader.face} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{p.leader.face.name}</div>
                    <div className="truncate text-xs text-text-3">
                      {pointsLabel(p.leader.score)} · {p.leader.season}
                      {p.leader.ended && ", ended"}
                    </div>
                  </div>
                </div>
              ) : p.value === null ? (
                <div className="mt-3 text-[13px] font-semibold text-text-3">{p.note ?? "later"}</div>
              ) : (
                <div className="mt-2">
                  <div className="font-jersey text-[34px] leading-none tabular-nums">{p.value.toLocaleString()}
                    {p.atLeast && "+"}
                  </div>
                  <div className="mt-1 text-[11.5px] text-text-3">{p.unit}</div>
                </div>
              )}
              {p.sub && <div className="mt-1 text-xs font-semibold text-muted-foreground">{p.sub}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Who owns a place, as the header says it: its brand, or "House place". */
export function ownerLabel(place: { brand: { name: string } | null }): string {
  return place.brand ? place.brand.name : "House place";
}

/** The whole page body for one place. `sample` marks fixture data;
 *  `freshness` (live reads only) drives the "updated Ns ago" line; `events` is
 *  the one-off read of how the last events went, `members` of its members,
 *  `standings` of its standings, `schedule` of its schedule. The schedule is
 *  collapsed unless `scheduleOpen`; opening it calls `onShowSchedule` (the page
 *  reads the staff-only groups then, once per visit, for staff only). The
 *  members' lists are collapsed unless `membersOpen`; opening them calls
 *  `onShowMembers` (the page reads the lists then, once per visit). The
 *  standings are collapsed unless `standingsOpen`; opening them calls
 *  `onShowStandings` (the page reads the tables after the first then, once per
 *  visit). `games` is the one-off read of its games, collapsed unless
 *  `gamesOpen` (opening reads nothing more); `gamesEditable`: the viewer can
 *  change the brand's list (its Games page), so the footer says where. `scheduling` (pop.in staff on a
 *  dev or preview build only) adds "Schedule an event" to the schedule;
 *  `scheduleFormOpen`, `scheduleFormStep`, `scheduleRowAction` (a row's Change
 *  or Cancel open), `scheduleRowStep` and `now` are for the proof. The
 *  Turnstile is collapsed unless `turnstileOpen`; it reads nothing of its own. */
export function PlaceToday({
  place,
  events,
  members,
  standings,
  games,
  schedule,
  sample,
  freshness,
  membersOpen = false,
  onShowMembers,
  standingsOpen = false,
  onShowStandings,
  gamesOpen = false,
  gamesEditable = false,
  scheduleOpen = false,
  onShowSchedule,
  turnstileOpen = false,
  scheduling,
  scheduleFormOpen = false,
  scheduleFormStep,
  scheduleRowAction = null,
  scheduleRowStep,
  now,
}: {
  place: PlaceData;
  events: Load<PlaceEvents | null>;
  members: Load<PlaceMembers | null>;
  standings: Load<PlaceStandings | null>;
  games: Load<PlaceGames | null>;
  schedule: Load<PlaceSchedule | null>;
  sample: boolean;
  freshness?: Freshness;
  membersOpen?: boolean;
  onShowMembers?: () => void;
  standingsOpen?: boolean;
  onShowStandings?: () => void;
  gamesOpen?: boolean;
  gamesEditable?: boolean;
  scheduleOpen?: boolean;
  onShowSchedule?: () => void;
  turnstileOpen?: boolean;
  scheduling?: ScheduleWrites;
  scheduleFormOpen?: boolean;
  scheduleFormStep?: FormStep;
  scheduleRowAction?: RowAction | null;
  scheduleRowStep?: FormStep | CancelStep;
  now?: number;
}) {
  const here = headcountOf(place);
  return (
    <div className="flex flex-col gap-5">
      {sample && (
        <div className="rounded-[10px] border border-dashed border-border-strong bg-panel-2 px-3.5 py-2.5 text-[12.5px] font-semibold text-muted-foreground">
          Sample data. Every number on this page is made up, for shaping the page.
        </div>
      )}
      <header className="flex items-end gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-bold uppercase tracking-[.06em] text-text-3">
            {ownerLabel(place)} · today
          </div>
          <h1 className="font-jersey text-[44px] leading-[1.05]">{place.name}</h1>
        </div>
        <div className="flex items-center gap-2 pb-2 text-[13px] font-semibold">
          <PresenceDot count={here} />
          {headcountLabel(place)} here now
          {sample && <SampleChip />}
        </div>
      </header>
      <RoomNow place={place} sample={sample} freshness={freshness} />
      <Schedule
        key={`schedule-${place.id}`}
        schedule={schedule}
        sample={sample}
        open={scheduleOpen}
        onShow={onShowSchedule}
        place={place}
        games={games}
        scheduling={scheduling}
        formOpen={scheduleFormOpen}
        formStep={scheduleFormStep}
        rowAction={scheduleRowAction}
        rowStep={scheduleRowStep}
        now={now}
      />
      <LastOnes events={events} sample={sample} />
      {/* Keyed by place: opening is per place visit. */}
      <People key={place.id} members={members} sample={sample} open={membersOpen} onShow={onShowMembers} />
      <Standings key={`standings-${place.id}`} standings={standings} sample={sample} open={standingsOpen} onShow={onShowStandings} />
      <Games key={`games-${place.id}`} games={games} place={place} sample={sample} open={gamesOpen} editable={gamesEditable} />
      <Turnstile key={`turnstile-${place.id}`} place={place} events={events} sample={sample} freshness={freshness} open={turnstileOpen} />
      <Parts place={place} events={events} members={members} standings={standings} games={games} sample={sample} freshness={freshness} />
    </div>
  );
}
